import { useEffect, useMemo, useState } from 'react';
import { UserPlus } from 'lucide-react';
import type {
  BrandId,
  InviteActivationSettings,
  InviteCode,
  InviteCodeStatus,
  UpdateInviteActivationSettingsRequest,
  UpdateInviteCodeQuotaRequest,
} from '@nadoutong/contracts';
import { BRAND_IDS, normalizeBrandId } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateInviteCode,
  useDisableInviteCode,
  useEnableInviteCode,
  useInviteActivationMintDefaults,
  useInviteActivationSettings,
  useInviteCodeSalespeople,
  useInviteCodes,
  useUpdateInviteActivationSettings,
  useUpdateInviteCodeQuota,
  useUpdateInviteCodeText,
} from '../api/inviteCodes.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatCnyCents, formatLocalTimestamp } from '../components/formatters.ts';
import { ProvisionSalespersonSheet } from '../components/ProvisionSalespersonSheet.tsx';
import { CustomInviteCodeField } from '../components/CustomInviteCodeField.tsx';
import { ModelPlanSelect } from '../components/ModelPlanSelect.tsx';
import {
  INVITE_CODE_MANAGE_ROLES,
  MODEL_PLAN_BIND_ROLES,
  ROLE_OPS_ADMIN,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import {
  CENTS_PER_YUAN,
  parseGrantYuanToCents,
  parsePositiveInt,
} from '../lib/inviteCodeOverrides.ts';
import {
  composeCustomInviteCode,
  describeInviteCodeTextError,
  formatInviteCodeFormatError,
  inviteCodeSuffixFromCode,
} from '../lib/customInviteCode.ts';
import { ownerDisplayLabel } from '../lib/ownerLabel.ts';
import { ActivationsDialog } from './invite-codes/ActivationsDialog.tsx';
import { RebindPlanDialog } from './invite-codes/RebindPlanDialog.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  TBody,
  THead,
  Table,
  TableEmpty,
  TableSkeleton,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';

/**
 * /crm/invite-codes — salesperson invite codes (epic #1475).
 *
 * One role-adaptive page over the three server-enforced scope tiers:
 *   - customer manager — read-only view of their OWN code and how much of
 *     today's quota it has burned;
 *   - sales lead — every code in their sales-profile brand, plus mint /
 *     retire / re-quota;
 *   - admin — cross-brand with a brand filter, the same actions, and the
 *     activation settings card (trial grant size + login-code limits).
 *
 * The row scope is injected by ops-api, never asserted here: this page
 * renders whatever the server decided the session may see. The action
 * gating below is UI affordance only — a CM who forged a request still
 * meets the `manageGate` on the server.
 *
 * `hasAnyOpsRole` with the MANAGE set is the right test even though role
 * expansion grants `ops-customer-manager` to every lead: expansion only
 * ever WIDENS, so a plain CM never gains `ops-sales-lead` and never
 * passes. (The reverse test — is this session a CM rather than a lead —
 * would need explicit membership, which is why ops-api uses it.)
 */

/**
 * A lead with no `sales_profiles` row has no brand partition, so ops-api
 * fails closed with a 403 rather than showing an unscoped list. That is a
 * fixable configuration problem, not a load failure — it gets its own
 * empty state pointing at the fix.
 */
function isSalesProfileMissing(error: unknown): boolean {
  return error instanceof OpsApiError && error.code === 'SALES_PROFILE_NOT_FOUND';
}

/**
 * The owner column reads as a person, not as `ops_…` (#1948). The shared
 * `#1098` chain ends at the ops user id, so a deleted or unnamed owner
 * degrades to the id rather than to a blank cell — and the id stays on the
 * row's `title` either way, so the troubleshooting handle never disappears.
 */
function ownerLabel(row: InviteCode): string {
  return ownerDisplayLabel({
    display_name: row.owner_display_name,
    email: row.owner_email,
    ops_user_id: row.owner_ops_user_id,
  });
}

type StatusFilter = InviteCodeStatus | 'all';

const STATUS_FILTERS: readonly StatusFilter[] = ['all', 'active', 'disabled'];

function statusFilterLabelKey(value: StatusFilter): I18nKey {
  if (value === 'active') return 'invite_codes.filter.active';
  if (value === 'disabled') return 'invite_codes.filter.disabled';
  return 'invite_codes.filter.all';
}

function isStatusFilter(value: string): value is StatusFilter {
  return STATUS_FILTERS.some((candidate) => candidate === value);
}

/**
 * Admin's brand filter, same shape as the status one. `'all'` is the default
 * because an admin's job here is cross-brand: pinning them to one brand on
 * arrival hid every other brand's codes behind a control they had no reason
 * to touch (#1985). It sends no `brand` param at all — ops-api already
 * treats an admin with no brand as unfiltered.
 */
type BrandFilter = BrandId | 'all';

const BRAND_FILTERS: readonly BrandFilter[] = ['all', ...BRAND_IDS];

function isBrandFilter(value: string): value is BrandFilter {
  return BRAND_FILTERS.some((candidate) => candidate === value);
}

const OWNER_FILTER_ALL = 'all';

interface OwnerFilterOption {
  readonly value: string;
  readonly label: string;
}

/**
 * One option per distinct owner on the list already loaded. The filter is
 * client-side — ops-api's list query stays status + brand — so the choices
 * can only be people present in that response. Same display chain as the
 * owner column, then alphabetical by that label.
 */
function ownerFilterOptions(codes: readonly InviteCode[]): readonly OwnerFilterOption[] {
  const byId = new Map<string, string>();
  for (const row of codes) {
    if (!byId.has(row.owner_ops_user_id)) {
      byId.set(row.owner_ops_user_id, ownerLabel(row));
    }
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

/**
 * Which brand's salespeople the mint dialog offers by default. A PRODUCT
 * decision, not `DEFAULT_BRAND_ID`: new codes are minted for nxtpath unless
 * the admin says otherwise. Deliberately independent of the page's brand
 * filter — narrowing what you read is not a statement about what you mint.
 */
const MINT_DEFAULT_BRAND: BrandId = 'nxtpath';

export function InviteCodesPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user, status } = useSession();
  const roles = user?.roles ?? [];
  const isAdmin = hasAnyOpsRole(roles, [ROLE_OPS_ADMIN]);
  const canManage = hasAnyOpsRole(roles, INVITE_CODE_MANAGE_ROLES);
  const canBindPlan = hasAnyOpsRole(roles, MODEL_PLAN_BIND_ROLES);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>(OWNER_FILTER_ALL);
  const [adminBrand, setAdminBrand] = useState<BrandFilter>('all');
  const [mintOpen, setMintOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [pendingDisable, setPendingDisable] = useState<InviteCode | null>(null);
  const [pendingEnable, setPendingEnable] = useState<InviteCode | null>(null);
  const [pendingQuota, setPendingQuota] = useState<InviteCode | null>(null);
  const [pendingRename, setPendingRename] = useState<InviteCode | null>(null);
  const [pendingActivations, setPendingActivations] = useState<InviteCode | null>(null);
  const [pendingRebind, setPendingRebind] = useState<InviteCode | null>(null);

  // Scoped sessions send no brand — ops-api pins theirs. Only admin, which
  // is genuinely cross-brand, picks one, and only when narrowing.
  const query = useInviteCodes(
    {
      status: statusFilter === 'all' ? undefined : statusFilter,
      brand: isAdmin && adminBrand !== 'all' ? adminBrand : undefined,
    },
    status === 'authenticated',
  );
  const disable = useDisableInviteCode();
  const enable = useEnableInviteCode();

  const codes = query.data?.invite_codes ?? [];
  const owners = useMemo(() => ownerFilterOptions(codes), [codes]);
  // A status change or a disable can drop the selected owner from the
  // loaded list. Fall back for this render instead of keeping a value the
  // Select no longer offers — that combination paints the empty state.
  const effectiveOwnerFilter =
    ownerFilter !== OWNER_FILTER_ALL && owners.some((owner) => owner.value === ownerFilter)
      ? ownerFilter
      : OWNER_FILTER_ALL;
  const visibleCodes =
    effectiveOwnerFilter === OWNER_FILTER_ALL
      ? codes
      : codes.filter((row) => row.owner_ops_user_id === effectiveOwnerFilter);

  // Narrowing the brand changes which people the list can contain. A
  // selection from the previous brand would otherwise hide every row.
  useEffect(() => {
    setOwnerFilter(OWNER_FILTER_ALL);
  }, [adminBrand]);

  function confirmDisable(): void {
    if (pendingDisable === null) return;
    disable.mutate(pendingDisable.id, {
      onSuccess: () => {
        setPendingDisable(null);
        toast.success(t('invite_codes.toast.disabled'));
      },
      onError: (error) => {
        toast.error(coerceErrorMessage(error));
      },
    });
  }

  function confirmEnable(): void {
    if (pendingEnable === null) return;
    enable.mutate(pendingEnable.id, {
      onSuccess: () => {
        setPendingEnable(null);
        toast.success(t('invite_codes.toast.enabled'));
      },
      onError: (error) => {
        toast.error(coerceErrorMessage(error));
      },
    });
  }

  return (
    <section className="flex flex-col gap-8" data-testid="invite-codes-page">
      <PageHeader
        title={t('invite_codes.title')}
        sub={t('invite_codes.sub')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <Select
                value={adminBrand}
                onValueChange={(next) => {
                  if (isBrandFilter(next)) setAdminBrand(next);
                }}
                options={BRAND_FILTERS.map((value) => ({
                  value,
                  label: value === 'all' ? t('invite_codes.filter.brand_all') : value,
                }))}
                ariaLabel={t('invite_codes.brand_aria')}
                mono
                testId="invite-codes-brand-select"
              />
            ) : null}
            <Select
              value={statusFilter}
              onValueChange={(next) => {
                if (isStatusFilter(next)) setStatusFilter(next);
              }}
              options={STATUS_FILTERS.map((value) => ({
                value,
                label: t(statusFilterLabelKey(value)),
              }))}
              ariaLabel={t('invite_codes.filter.aria')}
              testId="invite-codes-status-filter"
            />
            <Select
              value={effectiveOwnerFilter}
              onValueChange={(next) => {
                if (next === OWNER_FILTER_ALL || owners.some((owner) => owner.value === next)) {
                  setOwnerFilter(next);
                }
              }}
              options={[
                { value: OWNER_FILTER_ALL, label: t('invite_codes.owner_filter.all') },
                ...owners,
              ]}
              ariaLabel={t('invite_codes.owner_filter.label')}
              testId="invite-codes-owner-filter"
            />
            {/*
              Gated on `isAdmin`, not the page's `canManage`: the underlying
              provisioning route is admin-only, so showing this to a sales-lead
              — who may mint codes — would render a button that can only 403.
              Secondary because minting a code is what this page is for; this
              is the escape hatch for "the salesperson doesn't exist yet".
            */}
            {isAdmin ? (
              <Button
                variant="secondary"
                onClick={() => setProvisionOpen(true)}
                data-testid="ops-users-open-provision"
              >
                <UserPlus aria-hidden="true" className="h-4 w-4" />
                {t('ops_users.action.provision')}
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="primary"
                onClick={() => setMintOpen(true)}
                data-testid="invite-codes-mint-open"
              >
                {t('invite_codes.mint.open')}
              </Button>
            ) : (
              <Chip tone="neutral" testId="invite-codes-readonly-chip">
                {t('invite_codes.readonly')}
              </Chip>
            )}
          </div>
        }
      />

      {query.isLoading ? <TableSkeleton rows={4} /> : null}

      {query.isError && isSalesProfileMissing(query.error) ? (
        <EmptyState
          title={t('invite_codes.no_profile.title')}
          hint={t('invite_codes.no_profile.hint')}
          testId="invite-codes-no-profile"
        />
      ) : null}

      {query.isError && !isSalesProfileMissing(query.error) ? (
        <EmptyState
          title={t('invite_codes.error.load')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="invite-codes-error"
        />
      ) : null}

      {query.isSuccess ? (
        <Table testId="invite-codes-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('invite_codes.column.code')}</Th>
              <Th>{t('invite_codes.column.owner')}</Th>
              <Th>{t('invite_codes.column.brand')}</Th>
              <Th>{t('invite_codes.column.status')}</Th>
              <Th num>{t('invite_codes.column.today')}</Th>
              <Th num>{t('invite_codes.column.quota')}</Th>
              <Th>{t('invite_codes.column.created')}</Th>
              <Th>{t('invite_codes.column.plan')}</Th>
              {/* Rendered for every tier, unlike before `canManage` gated it:
                  the activation list lives in here now, and a read-only CM
                  must still be able to open their own code's. */}
              <Th>{t('invite_codes.column.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {visibleCodes.length === 0 ? (
              <TableEmpty colSpan={9} testId="invite-codes-empty">
                {t('invite_codes.empty')}
              </TableEmpty>
            ) : null}
            {visibleCodes.map((code) => (
              <Tr key={code.id} data-testid={`invite-codes-row-${code.id}`}>
                <Td mono emphasis>
                  {code.code}
                </Td>
                <Td title={code.owner_ops_user_id}>{ownerLabel(code)}</Td>
                <Td mono>{code.brand}</Td>
                <Td>
                  <Chip
                    tone={code.status === 'active' ? 'ok' : 'neutral'}
                    testId={`invite-codes-status-${code.id}`}
                  >
                    {t(
                      code.status === 'active'
                        ? 'invite_codes.status.active'
                        : 'invite_codes.status.disabled',
                    )}
                  </Chip>
                </Td>
                <Td num>{code.today_activation_count}</Td>
                <Td num>{code.effective_daily_quota}</Td>
                <Td>{formatLocalTimestamp(code.created_at)}</Td>
                <Td data-testid={`invite-codes-plan-${code.id}`}>
                  {code.model_plan === null
                    ? t('invite_codes.plan.none')
                    : code.model_plan.name}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPendingActivations(code)}
                      data-testid={`invite-codes-activations-${code.id}`}
                    >
                      {t('invite_codes.action.activations')}
                    </Button>
                    {canManage ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPendingQuota(code)}
                        data-testid={`invite-codes-quota-${code.id}`}
                      >
                        {t('invite_codes.action.quota')}
                      </Button>
                    ) : null}
                    {canManage ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPendingRename(code)}
                        data-testid={`invite-codes-rename-${code.id}`}
                      >
                        {t('invite_codes.action.rename')}
                      </Button>
                    ) : null}
                    {canBindPlan ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPendingRebind(code)}
                        data-testid={`invite-codes-rebind-${code.id}`}
                      >
                        {t('invite_codes.action.rebind')}
                      </Button>
                    ) : null}
                    {canManage ? (
                      code.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setPendingDisable(code)}
                          data-testid={`invite-codes-disable-${code.id}`}
                        >
                          {t('invite_codes.action.disable')}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingEnable(code)}
                          data-testid={`invite-codes-enable-${code.id}`}
                        >
                          {t('invite_codes.action.enable')}
                        </Button>
                      )
                    ) : null}
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      ) : null}

      {isAdmin ? <ActivationSettingsPanel /> : null}

      {canManage ? (
        <MintCodeDialog open={mintOpen} onOpenChange={setMintOpen} isAdmin={isAdmin} />
      ) : null}

      {isAdmin && provisionOpen ? (
        <ProvisionSalespersonSheet onClose={() => setProvisionOpen(false)} />
      ) : null}

      {pendingQuota !== null ? (
        <QuotaDialog code={pendingQuota} onClose={() => setPendingQuota(null)} />
      ) : null}

      {pendingRename !== null ? (
        <RenameCodeDialog code={pendingRename} onClose={() => setPendingRename(null)} />
      ) : null}

      {pendingActivations !== null ? (
        <ActivationsDialog
          code={pendingActivations}
          onClose={() => setPendingActivations(null)}
        />
      ) : null}

      {pendingRebind !== null ? (
        <RebindPlanDialog
          code={pendingRebind}
          onClose={() => setPendingRebind(null)}
        />
      ) : null}

      <ConfirmDialog
        open={pendingDisable !== null}
        onOpenChange={(open) => {
          if (!open && !disable.isPending) setPendingDisable(null);
        }}
        title={t('invite_codes.disable.title')}
        body={t('invite_codes.disable.body', { code: pendingDisable?.code ?? '' })}
        confirmLabel={t('invite_codes.action.disable')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={confirmDisable}
        tone="danger"
        loading={disable.isPending}
        testId="invite-codes-disable-confirm"
      />

      <ConfirmDialog
        open={pendingEnable !== null}
        onOpenChange={(open) => {
          if (!open && !enable.isPending) setPendingEnable(null);
        }}
        title={t('invite_codes.enable.title')}
        body={t('invite_codes.enable.body', { code: pendingEnable?.code ?? '' })}
        confirmLabel={t('invite_codes.action.enable')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={confirmEnable}
        loading={enable.isPending}
        testId="invite-codes-enable-confirm"
      />
    </section>
  );
}

interface MintCodeDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Admin sees an extra sentence disowning the brand filter. Only admin
   * has that filter on screen, so only admin can be misled by it.
   */
  readonly isAdmin: boolean;
}

/**
 * Mint one code for a salesperson. The owner is picked from same-brand
 * candidates (`GET .../salespeople`) rather than typed, since a lead has no
 * other way to know an ops user id and admin's is genuinely cross-brand.
 *
 * The code TEXT is optional. An empty suffix lets platform-api generate it;
 * a filled suffix is sent as `code` after `composeCustomInviteCode`. The
 * brand actually minted into is still the OWNER's `sales_profiles` row —
 * `CreateInviteCodeRequest` has no brand field. The brand selector here only
 * scopes which candidates admin sees; a lead's candidate list is always
 * their own server-resolved brand. The prefix shown beside the suffix is
 * the selected salesperson's brand, the admin's selected brand before that,
 * or the lead's first candidate.
 *
 * That selector owns its own state rather than borrowing the page's brand
 * FILTER (#1985): the two used to be one value, so narrowing the list also
 * moved the mint target, and an admin reading one brand could mint into it
 * without ever having chosen to.
 */
function MintCodeDialog({ open, onOpenChange, isAdmin }: MintCodeDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const create = useCreateInviteCode();
  const defaultsQuery = useInviteActivationMintDefaults(open);
  const [brand, setBrand] = useState<BrandId>(MINT_DEFAULT_BRAND);
  const salespeople = useInviteCodeSalespeople(isAdmin ? brand : undefined, open);
  const [owner, setOwner] = useState('');
  const [grantYuan, setGrantYuan] = useState('');
  const [expiryDays, setExpiryDays] = useState('');
  const [quota, setQuota] = useState('');
  const [planId, setPlanId] = useState<string | null>(null);
  const [customSuffix, setCustomSuffix] = useState('');
  const [customCodeError, setCustomCodeError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const salespersonRows = salespeople.data?.salespeople ?? [];
  const salespeopleOptions = salespersonRows.map((person) => ({
    value: person.ops_user_id,
    label: person.display_name,
  }));
  const ownerSelectDisabled = salespeople.isLoading || salespeopleOptions.length === 0;
  const defaults = defaultsQuery.data?.defaults ?? null;
  const selectedSalesperson =
    salespersonRows.find((person) => person.ops_user_id === owner) ?? null;
  // Prefix follows the person who will own the code. Before that pick, admin
  // sees the brand they selected and a lead sees their first candidate.
  const customCodeBrand =
    selectedSalesperson !== null
      ? selectedSalesperson.brand
      : isAdmin
        ? brand
        : (salespersonRows[0]?.brand ?? null);

  useEffect(() => {
    if (!open) {
      setPrefilled(false);
      return;
    }
    if (prefilled || defaults === null) return;
    setGrantYuan((defaults.grant_amount_cny_cents / CENTS_PER_YUAN).toFixed(2));
    setExpiryDays(String(defaults.grant_expiry_days));
    setQuota(String(defaults.default_daily_quota));
    setPrefilled(true);
  }, [open, prefilled, defaults]);

  function close(): void {
    setOwner('');
    setGrantYuan('');
    setExpiryDays('');
    setQuota('');
    setPlanId(null);
    setCustomSuffix('');
    setCustomCodeError(null);
    setBrand(MINT_DEFAULT_BRAND);
    setPrefilled(false);
    setValidationError(null);
    onOpenChange(false);
  }

  function submit(): void {
    const trimmedOwner = owner.trim();
    if (trimmedOwner.length === 0) {
      setValidationError(t('invite_codes.mint.error.owner'));
      return;
    }
    if (defaults === null) {
      setValidationError(t('invite_codes.mint.error.defaults'));
      return;
    }
    const parsedGrant = parseGrantYuanToCents(grantYuan);
    if (parsedGrant === null) {
      setValidationError(t('invite_codes.mint.error.grant'));
      return;
    }
    if (parsedGrant > defaults.max_grant_amount_cny_cents) {
      setValidationError(
        t('invite_codes.mint.error.grant_cap', {
          max: formatCnyCents(defaults.max_grant_amount_cny_cents, false),
        }),
      );
      return;
    }
    const parsedExpiry = parsePositiveInt(expiryDays);
    if (parsedExpiry === null) {
      setValidationError(t('invite_codes.mint.error.expiry'));
      return;
    }
    const parsedQuota = parsePositiveInt(quota);
    if (parsedQuota === null) {
      setValidationError(t('invite_codes.mint.error.quota'));
      return;
    }
    if (customCodeBrand === null) {
      // A lead with no loaded salesperson has no brand to prefix. An empty
      // suffix still means "let the server generate", so that path stays a
      // no-request return. A typed suffix with nowhere to attach it is the
      // missing-owner case — say so instead of swallowing the click.
      if (customSuffix.trim().length > 0) {
        setValidationError(t('invite_codes.mint.error.owner'));
      }
      setCustomCodeError(null);
      return;
    }
    const composed = composeCustomInviteCode(customSuffix, customCodeBrand);
    if (composed.kind === 'invalid') {
      setCustomCodeError(formatInviteCodeFormatError(t, customCodeBrand));
      return;
    }
    setCustomCodeError(null);
    setValidationError(null);
    const mintedCode = composed.kind === 'ok' ? composed.code : null;
    create.mutate(
      {
        owner_ops_user_id: trimmedOwner,
        grant_amount_cny_cents_override: parsedGrant,
        grant_expiry_days_override: parsedExpiry,
        daily_quota_override: parsedQuota,
        ...(planId === null ? {} : { model_plan_id: planId }),
        ...(mintedCode === null ? {} : { code: mintedCode }),
      },
      {
        onSuccess: (response) => {
          close();
          toast.success(
            t('invite_codes.mint.toast', { code: response.invite_code.code }),
          );
        },
        onError: (error) => {
          const specific = describeInviteCodeTextError(error, t, {
            code: mintedCode ?? '',
            brand: customCodeBrand,
            takenKey: 'invite_codes.custom_code.error.taken',
          });
          setValidationError(specific ?? coerceErrorMessage(error));
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !create.isPending) close();
      }}
      title={t('invite_codes.mint.title')}
      description={t(
        isAdmin ? 'invite_codes.mint.description_admin' : 'invite_codes.mint.description',
      )}
      closeLabel={t('common.close')}
      testId="invite-codes-mint-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={create.isPending}
            disabled={ownerSelectDisabled || !prefilled}
            data-testid="invite-codes-mint-submit"
          >
            {t('invite_codes.mint.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {isAdmin ? (
          <FormField
            label={t('invite_codes.brand_aria')}
            fieldId="invite-codes-mint-brand"
          >
            <Select
              id="invite-codes-mint-brand"
              value={brand}
              onValueChange={(next) => {
                const parsed = normalizeBrandId(next);
                if (parsed !== null) {
                  setBrand(parsed);
                  // The picked owner belongs to the old brand's candidate
                  // list; keeping it would mint into a brand nobody chose.
                  // The suffix was typed against the old prefix too.
                  setOwner('');
                  setPlanId(null);
                  setCustomSuffix('');
                  setCustomCodeError(null);
                }
              }}
              options={BRAND_IDS.map((id) => ({ value: id, label: id }))}
              ariaLabel={t('invite_codes.brand_aria')}
              mono
              testId="invite-codes-mint-brand"
            />
          </FormField>
        ) : null}
        <FormField
          label={t('invite_codes.mint.owner_label')}
          fieldId="invite-codes-mint-owner"
          hint={
            salespeople.isError
              ? t('invite_codes.mint.owner_load_error')
              : ownerSelectDisabled
                ? t('invite_codes.mint.owner_empty')
                : t('invite_codes.mint.owner_hint')
          }
        >
          <Select
            id="invite-codes-mint-owner"
            value={owner}
            onValueChange={setOwner}
            options={salespeopleOptions}
            placeholder={t('invite_codes.mint.owner_placeholder')}
            disabled={ownerSelectDisabled}
            ariaLabel={t('invite_codes.mint.owner_label')}
            testId="invite-codes-mint-owner"
          />
        </FormField>
        <CustomInviteCodeField
          fieldId="invite-codes-mint-code"
          testId="invite-codes-mint-code"
          label={t('invite_codes.custom_code.label')}
          hintKey="invite_codes.custom_code.hint"
          brand={customCodeBrand}
          value={customSuffix}
          onChange={(next) => {
            setCustomSuffix(next);
            setCustomCodeError(null);
          }}
          error={customCodeError}
          disabled={create.isPending}
        />
        <FormField
          label={t('invite_codes.mint.grant_label')}
          fieldId="invite-codes-mint-grant"
          hint={t('invite_codes.mint.grant_hint')}
        >
          <Input
            id="invite-codes-mint-grant"
            value={grantYuan}
            onChange={(event) => setGrantYuan(event.currentTarget.value)}
            inputMode="decimal"
            data-testid="invite-codes-mint-grant"
          />
        </FormField>
        <FormField
          label={t('invite_codes.mint.expiry_label')}
          fieldId="invite-codes-mint-expiry"
          hint={t('invite_codes.mint.expiry_hint')}
        >
          <Input
            id="invite-codes-mint-expiry"
            value={expiryDays}
            onChange={(event) => setExpiryDays(event.currentTarget.value)}
            inputMode="numeric"
            data-testid="invite-codes-mint-expiry"
          />
        </FormField>
        <FormField
          label={t('invite_codes.mint.quota_label')}
          fieldId="invite-codes-mint-quota"
          hint={t('invite_codes.mint.quota_hint')}
        >
          <Input
            id="invite-codes-mint-quota"
            value={quota}
            onChange={(event) => setQuota(event.currentTarget.value)}
            inputMode="numeric"
            data-testid="invite-codes-mint-quota"
          />
        </FormField>
        <ModelPlanSelect
          brand={isAdmin ? brand : undefined}
          value={planId}
          onChange={setPlanId}
          allowNone
          fieldId="invite-codes-mint-plan"
          label={t('invite_codes.mint.plan_label')}
          hint={t('invite_codes.mint.plan_hint')}
          testId="invite-codes-mint-plan"
        />
        {defaultsQuery.isError ? (
          <p
            role="alert"
            data-testid="invite-codes-mint-defaults-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {t('invite_codes.mint.error.defaults')}
          </p>
        ) : null}
        {validationError !== null ? (
          <p
            role="alert"
            data-testid="invite-codes-mint-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {validationError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

interface QuotaDialogProps {
  readonly code: InviteCode;
  readonly onClose: () => void;
}

/**
 * Edit one code's grant / expiry / daily-quota. Unchanged grant fields are
 * omitted so a NULL legacy code keeps inheriting. "Restore recommended"
 * refills grant, expiry, and quota.
 */
export function QuotaDialog({ code, onClose }: QuotaDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const update = useUpdateInviteCodeQuota();
  const defaultsQuery = useInviteActivationMintDefaults(true);
  const [grantYuan, setGrantYuan] = useState(
    (
      (code.grant_amount_cny_cents_override ?? code.effective_grant_amount_cny_cents) /
      CENTS_PER_YUAN
    ).toFixed(2),
  );
  const [expiryDays, setExpiryDays] = useState(
    String(code.grant_expiry_days_override ?? code.effective_grant_expiry_days),
  );
  const [quota, setQuota] = useState(
    String(code.daily_quota_override ?? code.effective_daily_quota),
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const maxGrant = defaultsQuery.data?.defaults.max_grant_amount_cny_cents ?? null;
  const storedGrant = code.grant_amount_cny_cents_override;
  const initialGrantCents =
    code.grant_amount_cny_cents_override ?? code.effective_grant_amount_cny_cents;
  const initialExpiryDays =
    code.grant_expiry_days_override ?? code.effective_grant_expiry_days;
  const initialQuota = code.daily_quota_override ?? code.effective_daily_quota;

  function restoreRecommended(): void {
    const recommended = defaultsQuery.data?.defaults;
    if (recommended === undefined) return;
    setGrantYuan((recommended.grant_amount_cny_cents / CENTS_PER_YUAN).toFixed(2));
    setExpiryDays(String(recommended.grant_expiry_days));
    setQuota(String(recommended.default_daily_quota));
    setValidationError(null);
  }

  function submit(): void {
    const parsedGrant = parseGrantYuanToCents(grantYuan);
    if (parsedGrant === null) {
      setValidationError(t('invite_codes.quota.error.grant'));
      return;
    }
    if (
      parsedGrant !== storedGrant &&
      maxGrant !== null &&
      parsedGrant > maxGrant
    ) {
      setValidationError(
        t('invite_codes.quota.error.grant_cap', { max: formatCnyCents(maxGrant, false) }),
      );
      return;
    }
    const parsedExpiry = parsePositiveInt(expiryDays);
    if (parsedExpiry === null) {
      setValidationError(t('invite_codes.quota.error.expiry'));
      return;
    }
    const parsedQuota = parsePositiveInt(quota);
    if (parsedQuota === null) {
      setValidationError(t('invite_codes.quota.error.quota'));
      return;
    }
    const body: UpdateInviteCodeQuotaRequest = {};
    if (parsedGrant !== initialGrantCents) {
      body.grant_amount_cny_cents_override = parsedGrant;
    }
    if (parsedExpiry !== initialExpiryDays) {
      body.grant_expiry_days_override = parsedExpiry;
    }
    if (parsedQuota !== initialQuota) {
      body.daily_quota_override = parsedQuota;
    }
    if (
      body.grant_amount_cny_cents_override === undefined &&
      body.grant_expiry_days_override === undefined &&
      body.daily_quota_override === undefined
    ) {
      onClose();
      return;
    }
    setValidationError(null);
    update.mutate(
      {
        id: code.id,
        body,
      },
      {
        onSuccess: () => {
          onClose();
          toast.success(t('invite_codes.quota.toast'));
        },
        onError: (error) => {
          setValidationError(coerceErrorMessage(error));
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !update.isPending) onClose();
      }}
      title={t('invite_codes.quota.title', { code: code.code })}
      closeLabel={t('common.close')}
      testId="invite-codes-quota-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={restoreRecommended}
            disabled={update.isPending || defaultsQuery.data === undefined}
            data-testid="invite-codes-quota-restore"
          >
            {t('invite_codes.quota.restore')}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={update.isPending}
            data-testid="invite-codes-quota-submit"
          >
            {t('invite_codes.quota.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField
          label={t('invite_codes.quota.grant_label')}
          fieldId="invite-codes-quota-grant"
          hint={t('invite_codes.quota.grant_hint')}
        >
          <Input
            id="invite-codes-quota-grant"
            value={grantYuan}
            onChange={(event) => setGrantYuan(event.currentTarget.value)}
            inputMode="decimal"
            data-testid="invite-codes-quota-grant"
          />
        </FormField>
        <FormField
          label={t('invite_codes.quota.expiry_label')}
          fieldId="invite-codes-quota-expiry"
          hint={t('invite_codes.quota.expiry_hint')}
        >
          <Input
            id="invite-codes-quota-expiry"
            value={expiryDays}
            onChange={(event) => setExpiryDays(event.currentTarget.value)}
            inputMode="numeric"
            data-testid="invite-codes-quota-expiry"
          />
        </FormField>
        <FormField
          label={t('invite_codes.quota.label')}
          fieldId="invite-codes-quota-input"
          hint={t('invite_codes.quota.hint')}
        >
          <Input
            id="invite-codes-quota-input"
            value={quota}
            onChange={(event) => setQuota(event.currentTarget.value)}
            inputMode="numeric"
            data-testid="invite-codes-quota-input"
          />
        </FormField>
        {validationError !== null ? (
          <p
            role="alert"
            data-testid="invite-codes-quota-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {validationError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

interface RenameCodeDialogProps {
  readonly code: InviteCode;
  readonly onClose: () => void;
}

/**
 * Replace one code's text. The previous text stops working as soon as the
 * write lands; already-activated customers keep their accounts. Submitting
 * the current text closes locally: nothing is sent and no toast is shown.
 */
export function RenameCodeDialog({ code, onClose }: RenameCodeDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const update = useUpdateInviteCodeText();
  const [suffix, setSuffix] = useState(inviteCodeSuffixFromCode(code.code));
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  function submit(): void {
    const composed = composeCustomInviteCode(suffix, code.brand);
    if (composed.kind !== 'ok') {
      setFieldError(formatInviteCodeFormatError(t, code.brand));
      return;
    }
    if (composed.code === code.code) {
      onClose();
      return;
    }
    setFieldError(null);
    setValidationError(null);
    update.mutate(
      { id: code.id, body: { code: composed.code } },
      {
        onSuccess: (response) => {
          onClose();
          toast.success(
            t('invite_codes.rename.toast', { code: response.invite_code.code }),
          );
        },
        onError: (error) => {
          const specific = describeInviteCodeTextError(error, t, {
            code: composed.code,
            brand: code.brand,
            takenKey: 'invite_codes.rename.error.taken',
          });
          setValidationError(specific ?? coerceErrorMessage(error));
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !update.isPending) onClose();
      }}
      title={t('invite_codes.rename.title', { code: code.code })}
      closeLabel={t('common.close')}
      testId="invite-codes-rename-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={update.isPending}
            disabled={update.isPending}
            data-testid="invite-codes-rename-submit"
          >
            {t('invite_codes.rename.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p
          role="note"
          data-testid="invite-codes-rename-warning"
          className="m-0 rounded-lg border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn"
        >
          {t('invite_codes.rename.warning', { code: code.code })}
        </p>
        <CustomInviteCodeField
          fieldId="invite-codes-rename-code"
          testId="invite-codes-rename-code"
          label={t('invite_codes.rename.label')}
          hintKey="invite_codes.rename.hint"
          brand={code.brand}
          value={suffix}
          onChange={(next) => {
            setSuffix(next);
            setFieldError(null);
          }}
          error={fieldError}
          disabled={update.isPending}
        />
        {validationError !== null ? (
          <p
            role="alert"
            data-testid="invite-codes-rename-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {validationError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

interface SettingsDraft {
  readonly grantAmount: string;
  readonly grantExpiryDays: string;
  readonly defaultDailyQuota: string;
  readonly maxGrantAmount: string;
  readonly loginCodeTtl: string;
  readonly resendInterval: string;
  readonly dailySendLimit: string;
  readonly maxAttempts: string;
}

function draftFromSettings(settings: InviteActivationSettings): SettingsDraft {
  return {
    grantAmount: (settings.grant_amount_cny_cents / CENTS_PER_YUAN).toFixed(2),
    grantExpiryDays: String(settings.grant_expiry_days),
    defaultDailyQuota: String(settings.default_daily_quota),
    maxGrantAmount: (settings.max_grant_amount_cny_cents / CENTS_PER_YUAN).toFixed(2),
    loginCodeTtl: String(settings.login_code_ttl_seconds),
    resendInterval: String(settings.login_code_resend_interval_seconds),
    dailySendLimit: String(settings.login_code_daily_send_limit),
    maxAttempts: String(settings.login_code_max_attempts),
  };
}

/**
 * Admin-only activation settings (epic #1475). Every knob is
 * CHECK-constrained positive in the schema and re-validated by
 * platform-api, so the draft parser below rejects the same shapes rather
 * than letting the operator discover it through a 400.
 *
 * The three mint recommended values only change what a NEW mint dialog
 * prefills (plus legacy NULL-override codes). login_code_* and the grant
 * cap take effect immediately. Issued grants are never restated.
 */
function ActivationSettingsPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const query = useInviteActivationSettings();
  const update = useUpdateInviteActivationSettings();
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const settings = query.data?.settings ?? null;
  const effectiveDraft = draft ?? (settings === null ? null : draftFromSettings(settings));

  /**
   * Field-at-a-time patch. The per-field ternaries are deliberate: a
   * computed key (`{ ...draft, [field]: value }`) widens to an index
   * signature under `noUncheckedIndexedAccess` and would need a cast to
   * land back on `SettingsDraft`.
   */
  function patchDraft(field: keyof SettingsDraft, value: string): void {
    const base = effectiveDraft;
    if (base === null) return;
    setDraft({
      grantAmount: field === 'grantAmount' ? value : base.grantAmount,
      grantExpiryDays: field === 'grantExpiryDays' ? value : base.grantExpiryDays,
      defaultDailyQuota: field === 'defaultDailyQuota' ? value : base.defaultDailyQuota,
      maxGrantAmount: field === 'maxGrantAmount' ? value : base.maxGrantAmount,
      loginCodeTtl: field === 'loginCodeTtl' ? value : base.loginCodeTtl,
      resendInterval: field === 'resendInterval' ? value : base.resendInterval,
      dailySendLimit: field === 'dailySendLimit' ? value : base.dailySendLimit,
      maxAttempts: field === 'maxAttempts' ? value : base.maxAttempts,
    });
    setValidationError(null);
  }

  function handleSave(): void {
    if (effectiveDraft === null) return;

    // The grant is entered in yuan but stored in cents. Reject anything
    // finer than a cent rather than silently rounding an operator's
    // typo into a real trial balance.
    const grantCents = parseGrantYuanToCents(effectiveDraft.grantAmount);
    if (grantCents === null) {
      setValidationError(t('invite_codes.settings.error.grant_amount'));
      return;
    }
    const maxGrantCents = parseGrantYuanToCents(effectiveDraft.maxGrantAmount);
    if (maxGrantCents === null) {
      setValidationError(t('invite_codes.settings.error.max_grant'));
      return;
    }
    if (grantCents > maxGrantCents) {
      setValidationError(t('invite_codes.settings.error.grant_above_cap'));
      return;
    }

    // Every remaining knob is CHECK-constrained positive in the schema.
    // Parsed one at a time so the message names the offending field.
    const grantExpiryDays = parsePositiveInt(effectiveDraft.grantExpiryDays);
    if (grantExpiryDays === null) {
      setValidationError(t('invite_codes.settings.error.grant_expiry'));
      return;
    }
    const defaultDailyQuota = parsePositiveInt(effectiveDraft.defaultDailyQuota);
    if (defaultDailyQuota === null) {
      setValidationError(t('invite_codes.settings.error.default_quota'));
      return;
    }
    const loginCodeTtl = parsePositiveInt(effectiveDraft.loginCodeTtl);
    if (loginCodeTtl === null) {
      setValidationError(t('invite_codes.settings.error.code_ttl'));
      return;
    }
    const resendInterval = parsePositiveInt(effectiveDraft.resendInterval);
    if (resendInterval === null) {
      setValidationError(t('invite_codes.settings.error.resend_interval'));
      return;
    }
    const dailySendLimit = parsePositiveInt(effectiveDraft.dailySendLimit);
    if (dailySendLimit === null) {
      setValidationError(t('invite_codes.settings.error.send_limit'));
      return;
    }
    const maxAttempts = parsePositiveInt(effectiveDraft.maxAttempts);
    if (maxAttempts === null) {
      setValidationError(t('invite_codes.settings.error.max_attempts'));
      return;
    }
    setValidationError(null);

    const body: UpdateInviteActivationSettingsRequest = {
      grant_amount_cny_cents: grantCents,
      grant_expiry_days: grantExpiryDays,
      default_daily_quota: defaultDailyQuota,
      max_grant_amount_cny_cents: maxGrantCents,
      login_code_ttl_seconds: loginCodeTtl,
      login_code_resend_interval_seconds: resendInterval,
      login_code_daily_send_limit: dailySendLimit,
      login_code_max_attempts: maxAttempts,
    };
    update.mutate(body, {
      onSuccess: () => {
        setDraft(null);
        toast.success(t('invite_codes.settings.toast.saved'));
      },
      onError: (error) => {
        toast.error(coerceErrorMessage(error));
      },
    });
  }

  const fields: readonly (readonly [keyof SettingsDraft, string, I18nKey, I18nKey])[] = [
    [
      'grantAmount',
      'invite-codes-settings-grant-amount',
      'invite_codes.settings.grant_amount_label',
      'invite_codes.settings.grant_amount_hint',
    ],
    [
      'grantExpiryDays',
      'invite-codes-settings-grant-expiry',
      'invite_codes.settings.grant_expiry_label',
      'invite_codes.settings.grant_expiry_hint',
    ],
    [
      'defaultDailyQuota',
      'invite-codes-settings-default-quota',
      'invite_codes.settings.default_quota_label',
      'invite_codes.settings.default_quota_hint',
    ],
    [
      'maxGrantAmount',
      'invite-codes-settings-max-grant',
      'invite_codes.settings.max_grant_label',
      'invite_codes.settings.max_grant_hint',
    ],
    [
      'loginCodeTtl',
      'invite-codes-settings-code-ttl',
      'invite_codes.settings.code_ttl_label',
      'invite_codes.settings.code_ttl_hint',
    ],
    [
      'resendInterval',
      'invite-codes-settings-resend-interval',
      'invite_codes.settings.resend_interval_label',
      'invite_codes.settings.resend_interval_hint',
    ],
    [
      'dailySendLimit',
      'invite-codes-settings-send-limit',
      'invite_codes.settings.send_limit_label',
      'invite_codes.settings.send_limit_hint',
    ],
    [
      'maxAttempts',
      'invite-codes-settings-max-attempts',
      'invite_codes.settings.max_attempts_label',
      'invite_codes.settings.max_attempts_hint',
    ],
  ];

  return (
    <Panel
      caption={t('invite_codes.settings.panel')}
      testId="invite-codes-settings-panel"
    >
      {query.isLoading ? (
        <div className="p-4">
          <TableSkeleton rows={4} />
        </div>
      ) : null}

      {query.isError ? (
        <EmptyState
          title={t('invite_codes.settings.load_error')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="invite-codes-settings-error"
        />
      ) : null}

      {settings !== null && effectiveDraft !== null ? (
        <div className="flex flex-col gap-4 p-4">
          <p className="m-0 text-dense text-paper-low" data-testid="invite-codes-settings-note">
            {t('invite_codes.settings.snapshot_note', {
              current: formatCnyCents(settings.grant_amount_cny_cents, false),
            })}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map(([field, testId, labelKey, hintKey]) => (
              <FormField key={field} label={t(labelKey)} fieldId={testId} hint={t(hintKey)}>
                <Input
                  id={testId}
                  value={effectiveDraft[field]}
                  onChange={(event) => patchDraft(field, event.currentTarget.value)}
                  inputMode={
                    field === 'grantAmount' || field === 'maxGrantAmount' ? 'decimal' : 'numeric'
                  }
                  data-testid={testId}
                />
              </FormField>
            ))}
          </div>

          {validationError !== null ? (
            <p
              role="alert"
              data-testid="invite-codes-settings-validation-error"
              className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
            >
              {validationError}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(null);
                setValidationError(null);
              }}
              disabled={draft === null || update.isPending}
              data-testid="invite-codes-settings-reset"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={update.isPending}
              disabled={draft === null}
              data-testid="invite-codes-settings-save"
            >
              {t('invite_codes.settings.save')}
            </Button>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}
